import axios from 'axios';
import AccessToken from '@/services/auth';
import signature from '@/services/signature';

const ACCESS_TOKEN = AccessToken();
const BASE_URI = process.env.VUE_APP_API_BASE_URI;

let config = {
	headers: {
		Authorization: 'Bearer ' + ACCESS_TOKEN.token,
		TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
	}
};

function buildQuery(params = {}) {
	const query = new URLSearchParams();
	if (params.branch_id !== undefined && params.branch_id !== null && params.branch_id !== 'all') {
		query.append('branch_id', params.branch_id);
	}
	if (params.start) query.append('start', params.start);
	if (params.end) query.append('end', params.end);
	if (params.group_by) query.append('group_by', params.group_by);
	if (params.expense_category_id) query.append('expense_category_id', params.expense_category_id);
	if (params.supplier_id) query.append('supplier_id', params.supplier_id);
	return query.toString();
}

/**
 * Reports API
 *
 * Endpoint contract (backend to implement):
 *
 *   GET /v1/reports/sales-summary?branch_id&start&end
 *     {
 *       data: {
 *         summary: {
 *           gross_sales: number, net_sales: number,
 *           total_orders: number,
 *           cancelled_count: number, cancelled_percent: number,
 *           avg_orders_per_day: number, days_in_range: number
 *         },
 *         daily: [
 *           { date: 'YYYY-MM-DD', orders, gross, discount, net, vat }
 *         ]
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/sales-by-hour?branch_id&start&end
 *     {
 *       data: {
 *         hourly: [
 *           { hour_start: 0..23, label: '10:00-11:00', orders, revenue, avg }
 *         ]
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/profit?branch_id&start&end&group_by
 *     group_by ∈ { 'day', 'product', 'category', 'branch' }
 *     {
 *       data: {
 *         summary: {
 *           revenue, cost, profit, margin, total_orders, days_in_range,
 *           waste_cost,                 // total inventory cost of WASTE/SPOILAGE/EXPIRY/BREAKAGE in range
 *           internal_consumption_cost,  // total inventory cost of STAFF_MEAL + BUFFET_USAGE in range
 *           adjusted_profit,            // = revenue − cost − waste_cost − internal_consumption_cost
 *           adjusted_margin,            // = adjusted_profit / revenue * 100
 *         },
 *         trend: [ { date: 'YYYY-MM-DD', revenue, cost, profit, waste_cost?, internal_consumption_cost? } ],
 *         rows: [
 *           { key, label, category?, qty?, orders?, revenue, cost, profit, margin }
 *           // group_by=day|branch may optionally add waste_cost / internal_consumption_cost per row
 *         ]
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/sales-by-branch?branch_id&start&end
 *     {
 *       data: {
 *         branches: [ { branch_id, name, orders, revenue, percent } ]  // sorted revenue desc
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/product-sales?branch_id&start&end
 *     {
 *       data: {
 *         products:   [ { product_id, name, category, qty, revenue, percent } ], // sorted revenue desc
 *         categories: [ { category_id, name, revenue, percent } ]                // for the pie
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/category-sales?branch_id&start&end
 *     {
 *       data: {
 *         categories: [ { category_id, name, qty, revenue, percent } ]  // sorted revenue desc
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/payment-methods?branch_id&start&end
 *     {
 *       data: {
 *         methods: [ { code, label, count, amount, percent } ]  // sorted amount desc
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/discount-report?branch_id&start&end
 *     {
 *       data: {
 *         summary: { total_discount, discount_rate, orders_with_discount, avg_discount },
 *         types: [ { type, count, amount, percent } ]  // sorted amount desc
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/staff-performance?branch_id&start&end
 *     {
 *       data: {
 *         staff: [ { staff_id, name, orders, sales, avg, cancellations } ]  // sorted sales desc
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/cancelled-orders?branch_id&start&end
 *     {
 *       data: {
 *         summary: { total_cancelled, cancel_rate, lost_revenue },
 *         orders: [ { date: 'YYYY-MM-DD', order_no, cancelled_by, reason, amount } ]
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/stock-movement?branch_id&start&end
 *     {
 *       data: {
 *         items: [ {
 *           inventory_id, item, unit,
 *           opening, received, used, waste,
 *           staff_meal,    // qty consumed via STAFF_MEAL reason
 *           buffet_usage,  // qty consumed via BUFFET_USAGE reason
 *           adjustment,    // signed net of INVENTORY_COUNT + FOUND_ITEM + OTHER
 *           closing,       // = opening + received − used − waste − staff_meal − buffet_usage + adjustment
 *         } ]
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/material-usage?branch_id&start&end
 *     {
 *       data: {
 *         summary: { total_cost, items_count },
 *         items: [ {
 *           inventory_id, item, unit,
 *           used_sale,        // qty used by sold orders (recipe consumption)
 *           used_production,  // qty used as production input
 *           used_waste,       // qty lost to WASTE/SPOILAGE/EXPIRY/BREAKAGE
 *           used_internal,    // qty consumed via STAFF_MEAL + BUFFET_USAGE
 *           total_qty,        // = used_sale + used_production + used_waste + used_internal
 *           cost              // total inventory cost in THB
 *         } ]  // sorted by cost desc
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 *
 *   GET /v1/reports/waste-report?branch_id&start&end
 *     {
 *       data: {
 *         summary: { total_cost, total_items, waste_rate },  // waste-only buckets: WASTE/SPOILAGE/EXPIRY/BREAKAGE
 *         items: [ { inventory_id, item, qty, unit, reason, cost } ],
 *         internal_consumption: {                            // STAFF_MEAL + BUFFET_USAGE — surfaced separately
 *           summary: { total_cost, total_items },
 *           items: [ { inventory_id, item, qty, unit, reason, cost, type } ]  // type ∈ 'staff_meal' | 'buffet_usage'
 *         }
 *       },
 *       filter: { branches: [ { id, name } ] }
 *     }
 */
class ReportsApi {
	/**
	 * ทะเบียนรายงานที่ "ร้านนี้" เปิดใช้ — ยิงก่อน render หน้า /reports
	 * (../silver-spoon-api/docs/report-catalog.md)
	 *
	 *   GET /v1/reports
	 *     {
	 *       data: [
	 *         { code, name, icon, color, reports: [
	 *             { code, name, description, icon, color, bg_color, permission_group_name }
	 *         ] }
	 *       ]
	 *     }
	 *   name / description = คีย์ i18n ไม่ใช่คำแปล → ห่อ $t() ก่อนแสดงเสมอ
	 */
	async list(queryString = '') {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports' + signature(queryString), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async salesSummary(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/sales-summary' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async salesByHour(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/sales-by-hour' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async profit(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/profit' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async salesByBranch(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/sales-by-branch' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async platformSales(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/platform-sales' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async productSales(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/product-sales' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async categorySales(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/category-sales' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async paymentMethods(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/payment-methods' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async discountReport(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/discount-report' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async promotionPerformance(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/promotion-performance' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async staffPerformance(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/staff-performance' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async cancelledOrders(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/cancelled-orders' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async stockMovement(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/stock-movement' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async wasteReport(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/waste-report' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async materialUsage(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/material-usage' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async vatSalesList(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/vat-sales-list' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async expenseReport(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/expense-report' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async goodsReceiptReport(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/goods-receipt-report' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async vatPurchaseList(params = {}) {
		try {
			const response = await axios.get(BASE_URI + '/v1/reports/vat-purchase-list' + signature(buildQuery(params)), config);
			return response;
		} catch (err) {
			throw err;
		}
	}

	async taxInvoiceRegister(params = {}) {
		try {
			const query = new URLSearchParams();
			if (params.branch_id !== undefined && params.branch_id !== null && params.branch_id !== '') {
				query.append('branch_id', params.branch_id);
			}
			if (params.year) query.append('year', params.year);
			const response = await axios.get(BASE_URI + '/v1/reports/tax-invoice-register' + signature(query.toString()), config);
			return response;
		} catch (err) {
			throw err;
		}
	}
}

export const Reports = new ReportsApi();
